/**
 * O*NET attribution — required wherever O*NET Web Services data informs what a
 * person is reading, on screen, on paper and in exported files.
 *
 * USDOL/ETA's terms of service require the "O*NET in-it" badge linking to
 * services.onetcenter.org plus the trademark sentence. One component so the
 * wording is identical on every surface and never drifts; the wording itself
 * lives in `src/lib/onetAttribution.ts` so exporters can reuse it verbatim.
 *
 * Print: the wrapper carries `onet-attribution`, which the print rules in
 * `src/index.css` keep visible, unbroken and ink-legible even inside sections
 * that otherwise hide chrome.
 *
 * Display only. Nothing here feeds scoring.
 */
import {
  ONET_ATTRIBUTION_SENTENCE,
  ONET_BADGE_ALT,
  ONET_BADGE_SRC,
  ONET_BADGE_TITLE,
  ONET_SERVICES_URL,
} from "@/lib/onetAttribution";

export { ONET_ATTRIBUTION_SENTENCE };

function Badge({ className = "" }: { className?: string }) {
  return (
    <a
      href={ONET_SERVICES_URL}
      title={ONET_BADGE_TITLE}
      target="_blank"
      rel="noreferrer noopener"
      className={`onet-attribution-badge inline-flex shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${className}`}
    >
      <img
        src={ONET_BADGE_SRC}
        width={130}
        height={60}
        loading="lazy"
        crossOrigin="anonymous"
        style={{ width: 130, height: 60, border: "none" }}
        alt={ONET_BADGE_ALT}
      />
    </a>
  );
}

function Sentence({ className = "" }: { className?: string }) {
  return (
    <p className={`onet-attribution-text text-xs leading-relaxed text-muted-foreground ${className}`}>
      This site incorporates information from{" "}
      <a
        href={ONET_SERVICES_URL}
        target="_blank"
        rel="noreferrer noopener"
        className="font-medium text-primary underline underline-offset-2"
      >
        O*NET Web Services
      </a>{" "}
      by the U.S. Department of Labor, Employment and Training Administration (USDOL/ETA). O*NET
      <sup>®</sup> is a trademark of USDOL/ETA.
    </p>
  );
}

export function OnetAttribution({
  variant = "inline",
  className = "",
}: {
  /** `inline` = small print + badge; `card` = bordered block; `badge` = badge only. */
  variant?: "inline" | "card" | "badge";
  className?: string;
}) {
  if (variant === "badge") return <Badge className={`onet-attribution ${className}`} />;

  if (variant === "card") {
    return (
      <div
        className={`onet-attribution flex flex-col items-start gap-3 rounded-2xl border border-border/60 bg-muted/20 p-4 sm:flex-row sm:items-center ${className}`}
      >
        <Badge />
        <Sentence />
      </div>
    );
  }

  return (
    <div
      className={`onet-attribution flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3 ${className}`}
    >
      <Badge />
      <Sentence />
    </div>
  );
}

export default OnetAttribution;
